import axios from 'axios'
import videojs from 'video.js'
import { registerPluginOnce } from '../utils.js'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  source: null // required
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBTrickPlay extends Plugin {
  /**
   * Create a NFBTrickPlay plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.obj.merge(defaults, options)
    this.sprites = []
    this.basePath = this.options.source.substring(0, this.options.source.lastIndexOf('/')) + '/'
    this.imagePathPrefix = ''

    this.init()
  }

  async init () {
    this.log('Initializing NFB Trick Play plugin')
    try {
      const sourceData = await this.loadSourceM3u8(this.options.source)
      const imageStreamURI = this.getImageStreamURI(sourceData)
      const imageStreamData = await this.loadImageStreamM3u8(imageStreamURI)
      this.sprites = this.getSpritesFromImageStream(imageStreamData)
      this.log('Sprites', this.sprites)

      if (this.sprites.length) {
        this.player.ready(() => {
          import(/* webpackChunkName: "videojs-thumbnail-sprite" */ 'videojs-thumbnail-sprite').then(m => {
            if (this.player.hasPlugin('thumbnailSprite')) {
              this.player.thumbnailSprite({
                sprites: this.sprites,
                responsiveWidthLimit: 1000
              })
            }
          })
        })
      }
    } catch (e) {
      this.log(e.message)
    }
  }

  async loadSourceM3u8 (url) {
    this.log('Loading source file')
    return axios.get(url)
      .then(r => r.data)
      .catch(e => {
        return `Unable to load source file: ${e}.`
      })
  }

  getImageStreamURI (data) {
    this.log('Get Image Stream URI')
    const matches = data.match(/#EXT-X-IMAGE-STREAM-INF:.*,URI="(.*)"/)
    if (!matches) {
      throw new Error('No image stream URI found')
    }

    const path = matches[1]
    this.imagePathPrefix = path.substring(0, path.lastIndexOf('/')) + '/'

    return this.basePath + path
  }

  async loadImageStreamM3u8 (url) {
    this.log('Loading image stream file')
    return axios.get(url)
      .then(r => r.data)
      .catch(e => {
        return `Unable to load image stream file: ${e}.`
      })
  }

  getSpritesFromImageStream (data) {
    this.log('Get sprites from image stream')
    const regex = /(#EXTINF:(?<duration>[0-9.]*),\n#EXT-X-TILES:RESOLUTION=(?<width>[0-9]*)x(?<height>[0-9]*),LAYOUT=([0-9]*)x([0-9]*),DURATION=(?<interval>[0-9.]*)\n(?<filename>.*\.jpg.*))/g
    const matches = [...data.matchAll(regex)]

    const sprites = matches.map((match, i) => ({
      url: this.basePath + this.imagePathPrefix + match.groups.filename,
      start: i * match.groups.duration,
      duration: parseInt(match.groups.duration),
      interval: parseInt(match.groups.interval),
      width: parseInt(match.groups.width),
      height: parseInt(match.groups.height)
    }))

    return sprites
  }
}

// Define default values for the plugin's `state` object here.
NFBTrickPlay.defaultState = {}

// Include the version number.
NFBTrickPlay.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbTrickPlay', NFBTrickPlay)

export default NFBTrickPlay
